import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import ThirdPane from '../ThirdPane';
import ThirdPaneContent from '../ThirdPane/ThirdPaneContent';
import ThirdPaneHeader from '../ThirdPane/ThirdPaneHeader';
import JobHistory from '../Job/JobHistory';
import { Box, Flex } from 'rebass';
import HazardSurveyDetail from './HazardSurveyDetail';
import Button from '../Button/Button';
import { PortalWithState } from 'react-portal';
import { GET_HAZARD_SURVEY_JOB } from './queries';
import SendHazardSurveyModal from './SendHazardSurveyModal';
import { ResultOf } from 'gql.tada';
import { useHistory } from 'react-router-dom';
import useMode from '../../hooks/useMode';
import HazardSurveyResultsModal from './HazardSurveyResultsModal';
import SurveyStatusModal from './SurveyStatusModal';
import { HazardSurveyStatus } from '@/services/hazardSurvey';
import ThirdPaneFooter from '@/components/ThirdPane/ThirdPaneFooter';
import StatusChip from '@/components/Status/StatusChip';
import { constantToLabel } from '@ecosconnect/core/src/helpers/string';

type HazardSurveyThirdPaneProps = {
  jobId?: string;
  prevEnabled: boolean;
  nextEnabled: boolean;
  onPrev?: () => void;
  onNext?: () => void;
  onToggle?: (visible: boolean) => void;
  reload?: () => void;
};

export type JobWithHazardSurvey = ResultOf<
  typeof GET_HAZARD_SURVEY_JOB
>['jobs'][number];

export default function HazardSurveyThirdPane({
  jobId,
  reload,
  onPrev,
  onNext,
  onToggle,
  prevEnabled,
  nextEnabled,
}: HazardSurveyThirdPaneProps): JSX.Element {
  const mode = useMode();
  const history = useHistory();
  const {
    error: itemError,
    data: itemData,
    refetch,
  } = useQuery(GET_HAZARD_SURVEY_JOB, {
    skip: !jobId,
    variables: { where: { id: jobId } },
  });

  const [job, setJob] = useState<JobWithHazardSurvey>();
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<HazardSurveyStatus | null>(
    null
  );

  useEffect(() => {
    if (itemData && itemData?.jobs?.length) {
      setJob(itemData?.jobs[0] as JobWithHazardSurvey);
    }
  }, [jobId, itemData, setJob]);

  const surveyStatus = job?.waterHazardSurvey?.status as
    | HazardSurveyStatus
    | undefined;
  const isSubmitted =
    surveyStatus === 'IN_REVIEW' || surveyStatus === 'ACCEPTED';
  const isCancelled = surveyStatus === 'CANCELLED';

  if (itemError) {
    throw itemError;
  }

  return (
    <>
      {showResultsModal && jobId && (
        <HazardSurveyResultsModal
          key={jobId}
          jobId={jobId}
          onClose={() => setShowResultsModal(false)}
        />
      )}
      {pendingStatus && jobId && (
        <SurveyStatusModal
          jobId={jobId}
          newStatus={pendingStatus}
          onClose={(actionTaken) => {
            setPendingStatus(null);
            if (actionTaken) refetch();
          }}
        />
      )}
      <ThirdPane visible={!!jobId}>
        <ThirdPaneHeader
          onToggle={onToggle}
          title={
            job ? (
              <>
                {jobId} <StatusChip>{constantToLabel(surveyStatus)}</StatusChip>
              </>
            ) : (
              'Loading...'
            )
          }
          onPrev={onPrev}
          onNext={onNext}
          prevEnabled={prevEnabled}
          nextEnabled={nextEnabled}
          sx={{ gap: 2 }}
        >
          {job && (
            <>
              {surveyStatus !== 'ACCEPTED' && (
                <Button
                  icon="edit"
                  variant="outline"
                  onClick={() =>
                    history.push(`/${mode}/hazard-surveys/${jobId}/edit`)
                  }
                >
                  Edit Survey
                </Button>
              )}
              {!isSubmitted && !isCancelled && (
                <PortalWithState closeOnEsc>
                  {({ openPortal, closePortal, portal }) => (
                    <>
                      <Button
                        icon="mail"
                        variant="secondary"
                        onClick={openPortal}
                      >
                        Email Survey Request
                      </Button>
                      {portal(
                        <SendHazardSurveyModal
                          key={job.id}
                          jobId={job.id}
                          waterServiceId={
                            job.waterHazardSurvey?.waterService?.id
                          }
                          onClose={closePortal}
                        />
                      )}
                    </>
                  )}
                </PortalWithState>
              )}
            </>
          )}
        </ThirdPaneHeader>
        <ThirdPaneContent className="pane-content">
          {job && (
            <>
              <Flex
                flexDirection="row"
                minHeight={0}
                flexGrow={1}
                flexShrink={1}
              >
                <Flex
                  flexDirection="column"
                  flexGrow={1}
                  flexShrink={1}
                  height="100%"
                  width={600}
                  overflowY="scroll"
                >
                  {job?.waterHazardSurvey && (
                    <HazardSurveyDetail job={job} onChange={refetch} />
                  )}
                </Flex>
                <JobHistory
                  width={325}
                  sx={{
                    borderLeft: '1px solid',
                    borderLeftColor: 'tableBorder',
                  }}
                  jobId={job.id}
                  comments={job.comments}
                  statusChanges={job.statusChanges}
                  attachments={job.attachments}
                  jobAddress={job.address}
                  refetch={refetch}
                />
              </Flex>
            </>
          )}
        </ThirdPaneContent>
        <ThirdPaneFooter>
          {job && (
            <>
              {!isSubmitted && !isCancelled && (
                <Button
                  icon="check-circle"
                  variant="primary"
                  onClick={() => setShowResultsModal(true)}
                >
                  Enter Results
                </Button>
              )}

              {/* Survey workflow status actions */}
              {surveyStatus === 'IN_REVIEW' && (
                <>
                  <Button
                    icon="check-circle"
                    variant="success"
                    onClick={() => setPendingStatus('ACCEPTED')}
                  >
                    Accept
                  </Button>
                  <Button
                    icon="repeat"
                    variant="outline"
                    onClick={() => setPendingStatus('IN_PROGRESS')}
                  >
                    Reopen
                  </Button>
                </>
              )}
              {isCancelled && (
                <Button
                  icon="repeat"
                  variant="outline"
                  onClick={() => setPendingStatus('IN_PROGRESS')}
                >
                  Reopen
                </Button>
              )}
              {surveyStatus === 'ACCEPTED' && (
                <Button
                  icon="repeat"
                  variant="outline"
                  onClick={() => setPendingStatus('IN_REVIEW')}
                >
                  Reopen Review
                </Button>
              )}
              {/*{surveyStatus === 'IN_PROGRESS' && (*/}
              {/*  <Button*/}
              {/*    variant="outline"*/}
              {/*    onClick={() => setPendingStatus('IN_REVIEW')}*/}
              {/*  >*/}
              {/*    Set to In Review*/}
              {/*  </Button>*/}
              {/*)}*/}

              <Box flexGrow={1} textAlign="right">
                {/*{isSubmitted && (*/}
                {/*  <Button*/}
                {/*    mr={2}*/}
                {/*    icon="eye"*/}
                {/*    variant="secondary"*/}
                {/*    onClick={() => setShowResultsModal(true)}*/}
                {/*  >*/}
                {/*    View Results*/}
                {/*  </Button>*/}
                {/*)}*/}
                {(surveyStatus === 'NEW' ||
                  surveyStatus === 'IN_PROGRESS' ||
                  surveyStatus === 'IN_REVIEW') && (
                  <Button
                    icon="slash-circle"
                    variant="danger"
                    onClick={() => setPendingStatus('CANCELLED')}
                  >
                    Cancel Survey
                  </Button>
                )}
              </Box>
            </>
          )}
        </ThirdPaneFooter>
      </ThirdPane>
    </>
  );
}
